<template>
  <div
    data-track-content
    :class="`productview ${
      isGhost ? 'ghost' : ''
    } position-relative w-100 h-100`"
    :data-content-piece="`${name} - ${productId}`"
    :data-content-name="`${store}`"
    :data-content-target="`Page - ${url}`"
  >
    <div class="shadow-filter position-absolute w-100 h-100">
      <div class="shadow-shape w-100 h-100"></div>
    </div>
    <a
      class="kleding-item d-flex flex-column w-100 h-100 p-2 p-md-3"
      :href="url"
      @click="onClick"
    >
      <!-- Image -->
      <div class="image">
        <div
          :class="{
            'main-image': true,
            'image-ratio-1-1':
              !imageShowFull &&
              imageAspectRatio >= 0.9 &&
              imageAspectRatio <= 1.1,
            'image-ratio-4-3': !imageShowFull && imageAspectRatio > 1.1,
            'image-ratio-3-4':
              imageShowFull || imageAspectRatio < 0.9 || !imageAspectRatio,
          }"
        >
          <picture v-if="images && images.default && images.default.default">
            <source
              v-if="images.default.webp"
              type="image/webp"
              :srcset="getSrcSet('webp')"
              sizes="(min-width: 578px) 230px,
                (min-width: 768px) 185px,
                (min-width: 992px) 188px,
                (min-width: 1200) 233px,
                calc(50vw - 15px - 1rem - 0.5rem)"
            />
            <source
              v-if="images.big && images.big.default"
              :type="getMimeType(images.default.default)"
              :srcset="getSrcSet('default')"
              sizes="(min-width: 578px) 230px,
                (min-width: 768px) 185px,
                (min-width: 992px) 188px,
                (min-width: 1200) 233px,
                calc(50vw - 15px - 1rem - 0.5rem)"
            />
            <img
              :src="createImageUrl(images.default.default)"
              :loading="isLazyLoading ? 'lazy' : 'eager'"
              :alt="`${name} via ${store}`"
            />
          </picture>
          <img
            v-else
            :src="createImageUrl(image)"
            :loading="isLazyLoading ? 'lazy' : 'eager'"
            :alt="`${name} via ${store}`"
          />
        </div>

        <!-- Labels -->
        <div class="labels">
          <div
            v-if="hasGiftcard && $store.state.countryCode === 'NL'"
            class="giftcard-label"
            title="Dit product is te koop met de Fair Fashion Giftcard"
          >
            Fair Fashion<br />Giftcard
          </div>
          <div v-if="isDiscounted" class="sale-label">Sale</div>
          <div
            v-if="!isProduction"
            v-show="showProductRankingLabels"
            title="Interaction rate"
            class="debug-label"
          >
            IR:{{ item.interaction_rate_rounded }}
          </div>
          <div
            v-if="!isProduction"
            v-show="showProductRankingLabels"
            title="Outlink clicks"
            class="debug-label-2"
          >
            OC:{{ item.outlink_clicks_rounded }}
          </div>
        </div>

        <!-- Quick view -->
        <div
          v-if="!isGhost"
          ref="quickview"
          class="product-hover"
          @click.prevent.stop="openQuickview"
        >
          <span>
            <IconEye />
            Quick View
          </span>
        </div>
      </div>

      <!-- Product info -->
      <div class="description">
        <div class="my-3">
          <div class="d-flex">
            <!-- Product name -->
            <p class="product-name mb-0">
              <b>{{ name }}</b>
            </p>

            <!-- Favorite -->
            <div
              class="favorite flex-shrink-0"
              @click.prevent.stop="onFavorite"
            >
              <span
                :class="`favorite-heart ${
                  isFavorited ? 'favorite-active' : ''
                }`"
              >
                <IconFavorite :class="{ fill: isFavorited }" />
              </span>
            </div>
          </div>
          <span class="brand-store-info" :title="name">
            {{ brand }}
            <span v-if="brand !== store" class="brand-store-seperator">//</span>
            {{ brand === store ? '' : store }}
          </span>
        </div>
      </div>

      <div :class="{ bottom: true, 'old-price-padding': !!oldPrice }">
        <!-- Price -->
        <p v-if="oldPrice" class="price">
          <del class="old-price">{{ $formatCurrency(currency, oldPrice) }}</del>
          <b>{{ $formatCurrency(currency, price) }}</b>
        </p>
        <p v-else class="price">
          <b>{{ $formatCurrency(currency, price) }}</b>
        </p>

        <!-- Certifications -->
        <div class="certifications">
          <ul>
            <li
              v-for="label in labels"
              :key="label"
              class="cece-tooltip"
              :data-tooltip="$store.state.labels[label]"
            >
              <img
                :src="`/static/img/labels/${label}.png`"
                :alt="$store.state.labels[label]"
              />
            </li>
          </ul>
        </div>
      </div>
    </a>
  </div>
</template>

<script>
import IconFavorite from '../../../../templates/icons/favorite.svg';
import IconEye from '../../../../templates/icons/eye.svg';

export default {
  components: {
    IconFavorite,
    IconEye,
  },
  props: {
    objectId: String,
    productId: String,
    slug: String,
    name: String,
    store: String,
    brand: String,
    image: String,
    images: Object,
    imageAspectRatio: Number,
    imageShowFull: Boolean,
    currency: String,
    price: Number,
    oldPrice: Number,
    hasGiftcard: Boolean,
    isDiscounted: Boolean,
    labels: Array,
    isGhost: Boolean,
    isLazyLoading: Boolean,
    sendEvent: Function,
    item: Object,
  },
  computed: {
    isFavorited() {
      return this.$store.state.favorites.indexOf(this.productId) !== -1;
    },
    url() {
      return `/catalog/product/${this.productId}/${this.slug}/`;
    },
    isProduction() {
      return this.$store.state.django_env === 'production';
    },
    showProductRankingLabels() {
      return (
        typeof window === 'object' &&
        window.cece.debug &&
        window.cece.debug.showProductRankingLabels
      );
    },
  },
  methods: {
    onFavorite() {
      window.favorite.toggleFavorite(
        null,
        this.productId,
        this.store,
        this.name
      );
    },
    onClick() {
      if (this.sendEvent) {
        this.sendEvent('click', this.item, 'Click product');
      }
    },
    openQuickview() {
      window.fillModal(this.productId);
      window.tracking.trackEvent('QuickView', this.store, this.name);
      window.tracking.trackContentInteractionNode(
        this.$refs['quickview'],
        'QuickView'
      );
    },
    getMimeType(path) {
      if (path.endsWith('.jpg')) {
        return 'image/jpeg';
      } else if (path.endsWith('.png')) {
        return 'image/png';
      } else if (path.endsWith('.webp')) {
        return 'image/webp';
      }
      return '';
    },
    createImageUrl(src) {
      const { django_env, typesense } = this.$store.state;
      if (django_env == 'production') {
        return src;
      } else if (
        django_env !== 'production' &&
        typesense.collectionName.startsWith('production_') &&
        !src.startsWith('http') &&
        !src.startsWith('https://')
      ) {
        return `https://www.projectcece.nl${src}`;
      } else if (
        django_env !== 'production' &&
        django_env !== 'staging' &&
        typesense.collectionName.startsWith('staging_') &&
        !src.startsWith('http') &&
        !src.startsWith('https://')
      ) {
        return `https://staging.projectcece.nl${src}`;
      }
      return src;
    },
    getSrcSet(type) {
      if (this.images.big[type]) {
        return `${this.createImageUrl(
          this.images.default[type]
        )} 230w, ${this.createImageUrl(this.images.big[type])} 460w`;
      }
      return this.createImageUrl(this.images.default[type]);
    },
  },
};
</script>
